import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  UtensilsCrossed, ChevronLeft, ChevronRight, Sparkles, Loader2,
  Trash2, Save, Share2, TrendingUp,
} from "lucide-react";

const MEAL_TYPES = [
  { key: "breakfast", label: "Petit-déjeuner", emoji: "🌅" },
  { key: "lunch", label: "Déjeuner", emoji: "☀️" },
  { key: "dinner", label: "Dîner", emoji: "🌙" },
  { key: "snack1", label: "Collation 1", emoji: "🍎" },
  { key: "snack2", label: "Collation 2", emoji: "🥜" },
];

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

function shiftDate(dateStr, days) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

export default function MealLog() {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [descriptions, setDescriptions] = useState({});
  const [shareMeals, setShareMeals] = useState(false);
  const [monthDays, setMonthDays] = useState([]);
  const [aiAdvice, setAiAdvice] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAdvice, setShowAdvice] = useState(false);

  const month = date.slice(0, 7);

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const [mealsRes, monthRes] = await Promise.all([
        api.get("/meals", { params: { date } }),
        api.get("/meals/month", { params: { month } }),
      ]);
      setMeals(mealsRes.data || []);
      setMonthDays(monthRes.data || []);
      const desc = {};
      (mealsRes.data || []).forEach(m => { desc[m.meal_type] = m.description || ""; });
      setDescriptions(desc);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeals(); }, [date, month]);

  useEffect(() => {
    api.get("/auth/me").then(r => setShareMeals(!!r.data.share_meals)).catch(() => {});
  }, []);

  const totals = useMemo(() => {
    return meals.reduce((acc, m) => ({
      calories: acc.calories + (m.calories || 0),
      protein: acc.protein + (m.protein || 0),
      carbs: acc.carbs + (m.carbs || 0),
      fat: acc.fat + (m.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  }, [meals]);

  const getMeal = (type) => meals.find(m => m.meal_type === type);

  const handleSave = async (mealType) => {
    const description = (descriptions[mealType] || "").trim();
    setSaving(mealType);
    try {
      const existing = getMeal(mealType);
      if (existing) {
        await api.put(`/meals/${existing.meal_log_id}`, { description });
      } else {
        await api.post("/meals", { date, meal_type: mealType, description });
      }
      toast.success("Repas enregistré — analyse IA en cours...");
      await fetchMeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (meal) => {
    try {
      await api.delete(`/meals/${meal.meal_log_id}`);
      toast.success("Repas supprimé");
      fetchMeals();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleShareToggle = async (checked) => {
    try {
      const { data } = await api.put("/meals/sharing", { share_meals: checked });
      setShareMeals(data.share_meals);
      toast.success(checked ? "Journal partagé avec votre coach" : "Partage désactivé");
    } catch {
      toast.error("Erreur");
    }
  };

  const handleAiAdvice = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.get("/meals/ai-advice");
      setAiAdvice(data);
      setShowAdvice(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pas assez de données");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div data-testid="meal-log-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <UtensilsCrossed className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Journal Alimentaire</h1>
            <p className="text-slate-400 mt-1 text-sm">Décrivez vos repas — l'IA calcule vos macros</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleAiAdvice} disabled={aiLoading} className="gap-1.5 rounded-full">
          {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-purple-600" />}
          Conseils IA
        </Button>
      </div>

      {/* Date navigation */}
      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setDate(shiftDate(date, -1))} className="rounded-full">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm"
          />
          <Button variant="outline" size="icon" onClick={() => setDate(shiftDate(date, 1))} className="rounded-full">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {date !== today && (
            <Button variant="ghost" size="sm" onClick={() => setDate(today)} className="text-blue-600">Aujourd'hui</Button>
          )}
        </div>
        <p className="text-sm font-medium text-slate-600 capitalize">{formatDateLabel(date)}</p>
      </div>

      {/* Month calendar dots */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {Array.from({ length: new Date(month + "-01T12:00:00").getDate() ? new Date(parseInt(month.split("-")[0]), parseInt(month.split("-")[1]), 0).getDate() : 31 }, (_, i) => {
          const day = String(i + 1).padStart(2, "0");
          const dayStr = `${month}-${day}`;
          const hasEntry = monthDays.includes(dayStr);
          const isSelected = dayStr === date;
          return (
            <button
              key={dayStr}
              onClick={() => setDate(dayStr)}
              className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                isSelected ? "bg-green-600 text-white" : hasEntry ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-slate-50 text-slate-400 hover:bg-slate-100"
              }`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      {/* Daily totals */}
      <Card className="mt-6 border-green-100 bg-gradient-to-r from-green-50 to-white">
        <CardContent className="p-4 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-slate-900">Totaux du jour</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-white text-slate-700 border">{Math.round(totals.calories)} kcal</Badge>
            <Badge className="bg-white text-slate-700 border">{totals.protein.toFixed(1)}g prot.</Badge>
            <Badge className="bg-white text-slate-700 border">{totals.carbs.toFixed(1)}g gluc.</Badge>
            <Badge className="bg-white text-slate-700 border">{totals.fat.toFixed(1)}g lip.</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Share toggle */}
      <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Share2 className="w-4 h-4 text-slate-500" />
          <div>
            <p className="text-sm font-medium text-slate-900">Partager avec mon coach</p>
            <p className="text-xs text-slate-400">Votre coach pourra consulter votre journal</p>
          </div>
        </div>
        <Switch checked={shareMeals} onCheckedChange={handleShareToggle} />
      </div>

      {/* Meal entries */}
      <div className="mt-6 space-y-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="border-slate-100"><CardContent className="p-4"><Skeleton className="h-20 w-full" /></CardContent></Card>
          ))
        ) : MEAL_TYPES.map(mt => {
          const meal = getMeal(mt.key);
          const isSaving = saving === mt.key;
          return (
            <Card key={mt.key} className="border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <span>{mt.emoji}</span> {mt.label}
                  </h3>
                  {meal?.ai_status === "pending" && (
                    <Badge variant="outline" className="text-xs gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Analyse...</Badge>
                  )}
                  {meal?.ai_status === "done" && meal.calories > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      <Badge className="bg-green-50 text-green-700 border-green-100 text-xs">{Math.round(meal.calories)} kcal</Badge>
                      <Badge variant="secondary" className="text-xs">{meal.protein}g P</Badge>
                      <Badge variant="secondary" className="text-xs">{meal.carbs}g G</Badge>
                      <Badge variant="secondary" className="text-xs">{meal.fat}g L</Badge>
                    </div>
                  )}
                </div>
                <Textarea
                  value={descriptions[mt.key] ?? ""}
                  onChange={e => setDescriptions(prev => ({ ...prev, [mt.key]: e.target.value }))}
                  placeholder={`Décrivez votre ${mt.label.toLowerCase()}...`}
                  rows={2}
                  className="resize-none mb-3"
                />
                {meal?.foods?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {meal.foods.map((f, i) => (
                      <span key={i} className="text-xs bg-slate-50 rounded-full px-2.5 py-1 text-slate-600">
                        {f.emoji} {f.name} {f.qty && `(${f.qty})`}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleSave(mt.key)}
                    disabled={isSaving || !(descriptions[mt.key] || "").trim()}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full gap-1"
                  >
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Enregistrer
                  </Button>
                  {meal && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(meal)} className="text-red-500 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI Advice Dialog */}
      <Dialog open={showAdvice} onOpenChange={setShowAdvice}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" /> Analyse nutritionnelle IA
            </DialogTitle>
          </DialogHeader>
          {aiAdvice && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-3">
                <div className="text-3xl font-bold text-purple-600">{aiAdvice.score}/100</div>
                <p className="text-sm text-slate-600">{aiAdvice.resume}</p>
              </div>
              {aiAdvice.points_positifs?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-green-700 mb-1">Points positifs</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {aiAdvice.points_positifs.map((p, i) => <li key={i}>✓ {p}</li>)}
                  </ul>
                </div>
              )}
              {aiAdvice.axes_amelioration?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-amber-700 mb-1">Axes d'amélioration</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {aiAdvice.axes_amelioration.map((p, i) => <li key={i}>→ {p}</li>)}
                  </ul>
                </div>
              )}
              {aiAdvice.conseils?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-blue-700 mb-1">Conseils</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {aiAdvice.conseils.map((p, i) => <li key={i}>• {p}</li>)}
                  </ul>
                </div>
              )}
              {aiAdvice.idee_repas && (
                <div className="bg-purple-50 rounded-xl p-3 text-sm text-slate-700">
                  <strong>Idée repas :</strong> {aiAdvice.idee_repas}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}